<template>
  <div class="forum">
    <ForumHeader :has-notice="hasNotice" />
    <div class="thread-content">
      <div class="navigation">
        <div class="go-back">
          <a :href="forumPath">
            <i class="fas fa-chevron-up" />
            Back to list
          </a>
        </div>
        <ButtonGroup class="navigation-buttons">
          <Button
            :disabled="!previousPost"
            @click="backward"
          >
            <i class="fas fa-chevron-left" />
          </Button>
          <Button
            :disabled="!nextPost"
            @click="forward"
          >
            <i class="fas fa-chevron-right" />
          </Button>
        </ButtonGroup>
      </div>
      <div class="detail-page-wrapper">
        <div
          v-if="!isPostFound && !loadingCurrentPost"
          class="no-post-msg"
        >
          Sorry the post is not found.
        </div>
        <Spin
          v-else-if="loadingPost"
          fix
        >
          <i
            class="fas fa-spinner fa-spin"
            style="font-size: 40px; color: var(--color-green-thick);"
          />
          <p>Loading Post</p>
        </Spin>
        <template v-else>
          <div
            v-if="isPostEditable"
            class="post-edit"
          >
            <Tooltip
              v-if="!editPost"
              content="Edit Post"
              placement="bottom"
            >
              <i
                class="fas fa-pencil-alt edit-icon"
                @click="onEditPost"
              />
            </Tooltip>
            <div
              v-else
              class="icons"
            >
              <Tooltip
                content="Update Post"
                placement="bottom"
              >
                <i
                  class="fas fa-check submit"
                  @click="updatePost"
                />
              </Tooltip>
              <Tooltip
                content="Cancel Edition"
                placement="bottom"
              >
                <i
                  class="fas fa-times cancel"
                  @click="onCancelEditPost"
                />
              </Tooltip>
            </div>
          </div>
          <div class="title-and-tags">
            <h1 v-if="!editPost">
              {{ postData.title }}
            </h1>
            <div
              v-else
              class="title-row-edit"
            >
              <i-input
                v-model="postTitle"
                name="postTitle"
                placeholder="Place your Post Title"
                clearable
                class="title"
              />
            </div>
            <TagBar
              v-if="!editPost"
              :tags="postData.tags"
              @forumTagSelection="onForumTagSelection"
            />
            <div v-else>
              <Select
                v-model="tagsId"
                placeholder="Tag"
                multiple
                filterable
                clearable
                class="tags"
              >
                <OptionGroup
                  v-for="group in forumTags"
                  :key="group.label"
                  :label="group.label"
                >
                  <Option
                    v-for="item in group.value"
                    :key="item.id"
                    :value="item.id"
                  >
                    {{ item.name }}
                  </Option>
                </OptionGroup>
              </Select>
            </div>
          </div>
          <div class="detail-card">
            <div class="card">
              <div class="avatar">
                <AuthorAvatar
                  :author="postData.author"
                  size="large"
                />
              </div>
              <div class="content">
                <div class="header">
                  <div
                    class="author"
                    @click="onUserClick(postData.author)"
                  >
                    {{ postData.author.name }}
                  </div>
                  <div class="post-age">
                    {{ postAge }}
                    <ContentReactToolBar
                      target="post"
                      :post-id="postId"
                      :post-sticky="postData.sticky"
                      :action-status="postActionStatus"
                      :is-admin="isAdmin"
                      class="reply-toolbar"
                      @scroll-to-editor="scrollToEditor"
                    />
                  </div>
                </div>
                <ContentEditor
                  ref="postEditor"
                  v-model="postContent"
                  :default-content-json="postData.content"
                  :editable="editPost"
                />
                <p
                  v-show="postUpdatedDate"
                  class="updated-date"
                >
                  Edited: {{ postUpdatedDate }}
                </p>
                <PreviewContainer
                  :files="postFiles"
                />
              </div>
            </div>
          </div>
          <Spin
            v-if="loadingReplies"
            fix
          >
            <i
              class="fas fa-spinner fa-spin"
              style="font-size: 40px; color: var(--color-green-thick);"
            />
            <p>Loading Replies</p>
          </Spin>
          <div
            v-else
            class="detail-card"
          >
            <template>
              <ReplyCard
                v-for="item in postReplies"
                :key="`replycard-${item.id}`"
                :reply-data="item"
                :post-id="postId"
                :reply-id="item.id"
                :users="users"
                :action-status-reply="repliesActionStatus[item.id]"
                :files="item.files"
                :user-id="Number.parseInt(user.id)"
                :post-author-id="postData.author.id"
                :post-resolved="postData.resolved"
                :post-markup="postMarkup"
                class="reply-card"
              />
            </template>
          </div>
        </template>
      </div>
      <div v-if="!loadingPost">
        <div class="instruction">
          <span class="instruction-text">Type your reply below</span>
          <span class="instruction-emoji"> 🤓</span>
        </div>
        <Spin
          v-if="submittingReply"
          size="large"
        >
          <i
            class="fas fa-spinner fa-spin"
            style="font-size: 40px; color: var(--color-green-thick);"
          />
          <p style="color: var(--color-green-thick)">
            Submitting...
          </p>
        </Spin>
        <div v-else>
          <ContentEditor
            ref="postReplyEditor"
            v-model="postReplyContent"
            editable
          />
          <ForumReplyButtonSet
            @onSubmit="submitPostReply"
            @onCancel="clearEditor"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import axios from 'axios'
import moment from 'moment'
import { mapState, mapGetters } from 'vuex'

import ForumHeader from './helper-components/forum/ForumHeader'
import ContentEditor from './helper-components/forum/ContentEditor'
import TagBar from './helper-components/forum/TagBar'
import ContentReactToolBar from './helper-components/forum/ContentReactToolBar'
import ReplyCard from './helper-components/forum/ReplyCard'
import ForumReplyButtonSet from './helper-components/forum/ForumReplyButtonSet'
import AuthorAvatar from './helper-components/forum/AuthorAvatar'
import PreviewContainer from './helper-components/dropzone/Preview-Container'

import { cleanEmbeddedImagesWithinPost, saveAndReplaceImagesWithinReply, saveAndReplaceImagesWithinPost, isContentEmpty } from '@/helpers/forum'
import UserMixin from '@/mixins/UserMixin'
import { forumTagsConstant } from '@/constants/forum-tags.js'

const COMPONENT_REPLY = 'forum_reply'
const COMPONENT_POST = 'forum_post'
const DEFAULT_EDITOR_CONTENT = {
  JSON: {
    content: {}
  },
  HTML: '',
  IMAGES: null,
  ATTACHMENTS: null
}

export default {
  components: {
    ForumHeader,
    ContentEditor,
    TagBar,
    ContentReactToolBar,
    ReplyCard,
    ForumReplyButtonSet,
    AuthorAvatar,
    PreviewContainer
  },

  mixins: [
    UserMixin
  ],

  provide () {
    return {
      postActionStatus: this.postActionStatus,
      repliesActionStatus: this.repliesActionStatus
    }
  },

  props: {
    hasNotice: Boolean,
    userId: Number
  },

  data () {
    return {
      loadingPost: true,
      loadingReplies: true,
      loadingCurrentPost: true,
      postContent: _.cloneDeep(DEFAULT_EDITOR_CONTENT),
      postData: {},
      postReplyContent: _.cloneDeep(DEFAULT_EDITOR_CONTENT),
      postActionStatus: {
        isLike: false,
        isFavorite: false,
        likeCount: 0,
        likeId: null,
        favId: null
      },
      repliesActionStatus: {},
      user: null,
      postFiles: [],
      postTitle: '',
      editPost: false,
      subscribed: false,
      forumTags: forumTagsConstant,
      tagsId: [],
      submittingReply: false
    }
  },

  computed: {
    postMarkup () {
      return this.$refs.postEditor.editor.getHTML()
    },

    postAge () {
      const currentDate = moment()
      const createdDate = moment(this.postData.createdAt)

      if (currentDate.diff(createdDate, 'week') >= 1) {
        const date = moment(createdDate).format('YYYY-MM-DD')
        const time = moment(createdDate).format('hh:mm a')
        return `${date} at ${time}`
      }
      return moment(this.postData.createdAt).fromNow()
    },

    ...mapState('forum', {
      pageSize: 'pageSize',
      postReplies: 'postReplies',
      filteredThreads: 'filteredThreads',
      searchText: 'searchText',
      posts: 'postsMap',
      postsTags: 'postsTagsMap'
    }),

    ...mapGetters('forum', {
      threads: 'threadsArr'
    }),

    postId () {
      return Number(this.$route.params.id)
    },

    postEditEmbeddedImages () {
      return this.postContent.IMAGES || []
    },

    embeddedImages () {
      return this.postReplyContent.IMAGES || []
    },

    attachedFiles () {
      return this.postReplyContent.ATTACHMENTS || []
    },

    forumPath () {
      const currentPage = Math.floor(this.currentThreadIndex / this.pageSize) + 1
      const searchParam = this.searchText ? `&searchText=${this.searchText}` : ''
      return `/forum?page=${currentPage}${searchParam}`
    },

    /**
     * Returns the current posts index in the thread list context if it exists
     */
    currentThreadIndex () {
      if (this.filteredThreads) {
        return this.filteredThreads.findIndex(thread => thread.id === this.postId)
      } else {
        return -1
      }
    },

    /**
     * Returns the previous post data or null if not applicable
     */
    previousPost () {
      // don't show previous button if there is no thread context or if we're at the first post
      if (this.currentThreadIndex <= 0) {
        return null
      } else {
        return this.filteredThreads[this.currentThreadIndex - 1]
      }
    },

    /**
     * Returns the next post data or null if not applicable
     */
    nextPost () {
      // don't show next button if there is no thread context (-1) or if we're at the last post
      if (this.currentThreadIndex === -1 || this.currentThreadIndex === this.filteredThreads.length - 1) {
        return null
      } else {
        return this.filteredThreads[this.currentThreadIndex + 1]
      }
    },

    /**
     * Returns a "true" value if the current user is the author of the current post.
     */
    isPostEditable () {
      return Number.parseInt(this.user.id) === this.postData.author.id
    },

    /**
     * Returns the corresponding class for the edition button for Title.
     * NOTE: If the title is empty, it adds a "disabled" class.
     */
    updateTitleClass () {
      return `fas fa-check submit ${!this.postTitle.length ? ' disabled' : ''}`
    },

    /**
     * Returns the last updated datetime of a post.
     */
    postUpdatedDate () {
      // If there's no postData info or the Post creation date is the same than the updated date, then don't show the last updated info (return an empty string).
      if (!this.postData || this.postData.createdAt === this.postData.updatedAt) {
        return ''
      }

      // Get the Post updated date.
      const updatedDate = moment(this.postData.updatedAt)

      // Get the current date and extract the updated date and time.
      const currentDate = moment()
      const date = moment(updatedDate).format('YYYY-MM-DD')
      const time = moment(updatedDate).format('hh:mm a')

      // If it has been a week or more since last update, then show the date and time. Otherwise, show the elapsed time.
      return currentDate.diff(updatedDate, 'week') >= 1 ? `${date} at ${time}` : moment(updatedDate).fromNow()
    },

    /**
     * Returns a "true" value if the current user belongs to the Admin Users.
     */
    isAdmin () {
      return this.adminConsoleUsers.findIndex(admin => admin === this.user.email) >= 0
    },

    tagsList () {
      return this.forumTags.reduce((acc, cur) => {
        return [...acc, ...cur.value]
      }, [])
    },

    currentPost () {
      return this.posts[this.postId] || {}
    },

    isPostFound () {
      return this.posts.hasOwnProperty(this.postId)
    }
  },

  watch: {
    // subscribed: function () { todo rethink
    //   axios.post(`${process.env.VUE_APP_API_URI}/forum/subscribed/`, {
    //     userId: this.user.id,
    //     postId: this.postId
    //   })
    // },
    /**
     * Listen for post id changes to load next/previous post
     */
    '$route.params.id': async function () {
      if (this.isPostFound) {
        await this.setData()
      }
    },

    'currentPost.resolved' () {
      this.$set(this.postData, 'resolved', this.currentPost.resolved)
    },

    'postData.tags' () {
      this.tagsId = this.postData.tags.map(tag => this.tagsList.find(item => item.name === tag).id)
    }
  },

  async created () {
    // Fetch the specific post data for use if enter the url directly
    this.loadingCurrentPost = true
    if (!this.posts.hasOwnProperty(this.postId)) {
      const [currentPostData, currentPostTagsData] = await Promise.all([this.fetchPost(), this.fetchPostTags()])
      if (!_.isEmpty(currentPostData)) {
        this.$store.commit('forum/updatePostsMap', currentPostData)
        this.$store.commit('forum/updatePostsTagsMap', {
          postId: this.postId,
          postTagsData: currentPostTagsData
        })
      }
    }
    this.loadingCurrentPost = false

    // Only set the data for page if the post is valid (not checking for user logging in anymore)
    if (this.isPostFound) {
      this.setData()
    }
  },

  mounted () {
    this.$log('forum_view', 'post')
  },

  methods: {
    /**
     * Fetches the single post for detail page
     */
    async fetchPost () {
      try {
        return (await axios.get(`${process.env.VUE_APP_API_URI}/forum/post/${this.postId}`)).data
      } catch (error) {
        this.$Message.error(`Failed to fetch single post: ${error}`)
      }
    },

    /**
     * Fetches the tags for current post
     */
    async fetchPostTags () {
      try {
        return (await axios.get(`${process.env.VUE_APP_API_URI}/forum/post/${this.postId}/tags`)).data
      } catch (error) {
        this.$Message.error(`Failed to fetch post tags: ${error}`)
      }
    },

    /**
     * Fetches the post and related data (tags/replies)
     * When enter the page using link directly (no prop data)
     * Difference: no numReplies
     */
    async initData () {
      this.user = this.$auth.getUser()
      this.fetchAdminConsoleUsers()
      await this.fetchAllUsers()

      this.loadingPost = true
      // If comes from homepage, postData is passed to the PostThread component
      if (!_.isEmpty(this.$route.params.postData)) {
        const { id, title, content, author, createdAt, lastUpdated, tags, sticky, resolved } = this.$route.params.postData
        this.postData = { id, title, content, author, createdAt, updatedAt: lastUpdated, tags, sticky, resolved }
      } else {
        // If using URL directly or redirect to newly created Post
        const { id, title, authorId, createdAt, updatedAt, content, sticky, resolved } = this.currentPost
        const author = this.users.find(user => user.id === authorId)
        const tags = (this.postsTags[this.postId] || []).map(item => item.name)
        this.postData = { id, title, content, author, createdAt, updatedAt, tags, sticky, resolved }
      }
      this.postTitle = this.postData.title

      const postContentJSON = cleanEmbeddedImagesWithinPost(JSON.parse(this.postData.content))

      this.postData.content = JSON.stringify(postContentJSON)
      this.postContent.JSON = postContentJSON

      this.loadingPost = false

      // Init full filtered threads data, out of loading loop, used for forward/backfound navigation
      if (!this.threads.length) {
        await this.$store.dispatch('forum/fetchPosts')
        this.$store.dispatch('forum/getThreads', { users: this.users })
        this.$store.dispatch('forum/filterThreads')
      }
    },

    /**
     * Further setting the data to render the page
     */
    async setData () {
      await this.initData()
      await this.fetchPostReplies()
      this.fetchPostActionStatus()
      this.fetchRepliesActionStatus()
    },

    /**
     * Fetches all replies for the post
     */
    async fetchPostReplies () {
      this.loadingReplies = true
      await this.$store.dispatch('forum/fetchPostReplies', { postId: this.postId })
      this.loadingReplies = false
    },

    /**
     * Fetches user's action status towards post/reply
     * Regarding like/fav
     */
    async fetchPostActionStatus () {
      try {
        const likeStatusPromise = axios.get(`${process.env.VUE_APP_API_URI}/users/${this.user.id}/action-status`, {
          params: {
            actionType: 'like',
            componentType: COMPONENT_POST,
            componentIds: [this.postId]
          }
        })
        const favStatusPromise = axios.get(`${process.env.VUE_APP_API_URI}/users/${this.user.id}/action-status`, {
          params: {
            actionType: 'favorite',
            componentType: COMPONENT_POST,
            componentIds: [this.postId]
          }
        })
        const likeCountPromise = axios.get(process.env.VUE_APP_API_URI + '/user-actions/total-likes', {
          params: {
            componentType: COMPONENT_POST,
            componentId: this.postId
          }
        })
        const [likeStatus, favStatus, likeCount] = await Promise.all([likeStatusPromise, favStatusPromise, likeCountPromise])
        this.$set(this.postActionStatus, 'isLike', Boolean(likeStatus.data.length))
        this.$set(this.postActionStatus, 'isFavorite', Boolean(favStatus.data.length))
        this.$set(this.postActionStatus, 'likeCount', likeCount.data.length ? likeCount.data[0].total_likes : 0)
        this.$set(this.postActionStatus, 'likeId', likeStatus.data.length ? likeStatus.data[0].id : null)
        this.$set(this.postActionStatus, 'favId', favStatus.data.length ? favStatus.data[0].id : null)
      } catch (error) {
        console.error(`Failed to get post action status: ${error}`)
      }
    },

    async fetchRepliesActionStatus () {
      const replyIds = this.postReplies.map(reply => reply.id)
      replyIds.forEach(replyId => {
        this.$set(this.repliesActionStatus, replyId, {
          isLike: false,
          isFavorite: false,
          likeCount: 0,
          likeId: null,
          favId: null
        })
      })
      try {
        const likeStatusPromise = axios.get(`${process.env.VUE_APP_API_URI}/users/${this.user.id}/action-status`, {
          params: {
            actionType: 'like',
            componentType: COMPONENT_REPLY,
            componentIds: replyIds
          }
        })
        const favStatusPromise = axios.get(`${process.env.VUE_APP_API_URI}/users/${this.user.id}/action-status`, {
          params: {
            actionType: 'favorite',
            componentType: COMPONENT_REPLY,
            componentIds: replyIds
          }
        })

        const [likeStatus, favStatus] = await Promise.all([likeStatusPromise, favStatusPromise])
        const totalLikes = await axios.get(process.env.VUE_APP_API_URI + '/user-actions/group-total-likes', {
          params: {
            componentType: COMPONENT_REPLY,
            componentIds: replyIds
          }
        })
        likeStatus.data.forEach(result => {
          this.$set(this.repliesActionStatus[result.component_id], 'isLike', true)
          this.$set(this.repliesActionStatus[result.component_id], 'likeId', result.id)
        })
        favStatus.data.forEach(result => {
          this.$set(this.repliesActionStatus[result.component_id], 'isFavorite', true)
          this.$set(this.repliesActionStatus[result.component_id], 'favId', result.id)
        })
        totalLikes.data.forEach(result => {
          this.$set(this.repliesActionStatus[result.component_id], 'likeCount', result.total_likes)
        })
      } catch (error) {
        console.error(`Failed to get replies action status: ${error}`)
      }
    },

    /**
     * Clean the editor content after new reply submitted
     */
    clearEditor () {
      this.$nextTick(() => {
        this.$refs.postReplyEditor.editor.clearContent()
        this.postReplyContent = _.cloneDeep(DEFAULT_EDITOR_CONTENT)
      })
    },

    /**
     * Scroll to the bottom to navigate to the reply box for the post
     */
    scrollToEditor () {
      window.scrollTo(0, document.body.scrollHeight || document.documentElement.scrollHeight)
    },

    /**
     * Submit post reply to database
     * And update reply data instantly on page
     */
    async submitPostReply () {
      if (isContentEmpty(this.postReplyContent.HTML)) {
        this.$Message.error('Content cannot empty.')
        return
      }

      try {
        this.submittingReply = true
        // If there's any embedded image, replace it's content with it's file name in S3.
        if (this.embeddedImages.length) {
          const jsonContent = this.postReplyContent.JSON.content

          this.postReplyContent.JSON.content = await saveAndReplaceImagesWithinReply(jsonContent, this.embeddedImages)
        }

        const reply = {
          parentPostId: this.postId,
          parentReplyId: null,
          authorId: Number(this.$auth.getUser().id),
          content: this.postReplyContent.JSON,
          markup: this.postReplyContent.HTML
        }
        this.$log('forum_reply', reply.content)
        const addedReplyId = await this.$store.dispatch('forum/addReply', { reply, context: this.postMarkup })

        this.$Message.info('reply submitted!')
        this.submittingReply = false
        await this.$store.dispatch('forum/updatePostReplies', { addedReplyId })
        // update repliesActionStatus
        this.$set(this.repliesActionStatus, addedReplyId, {
          isLike: false,
          isFavorite: false,
          likeCount: 0,
          likeId: null,
          favId: null
        })
        this.clearEditor()
      } catch (error) {
        const message = `Error while submitting Post Reply: ${error.message}`
        this.$Message.error(message)
        console.error(message)
        this.submittingReply = false
      }
    },

    /**
     * Displays the previous post from current one.
     */
    backward () {
      this.$router.replace({
        name: 'Forum Post',
        params: {
          id: this.previousPost.id,
          postData: this.previousPost
        }
      })
    },

    /**
     * Displays the next post from current one.
     */
    forward () {
      this.$router.replace({
        name: 'Forum Post',
        params: {
          id: this.nextPost.id,
          postData: this.nextPost
        }
      })
    },

    onForumTagSelection (tag) {
      this.$router.replace({ ...this.$route, name: 'Forum', query: { ...this.$route.query, tags: tag } })
    },

    /**
     * Updates the title, tags and content of the post.
     */
    async updatePost () {
      // Validate title content
      if (!this.postTitle.trim()) {
        this.$Message.error('Post Title cannot be empty')
        return
      }

      try {
        if (this.postEditEmbeddedImages.length) {
          const jsonContent = this.postContent.JSON.content
          this.postContent.JSON.content = await saveAndReplaceImagesWithinPost(jsonContent, this.postEditEmbeddedImages)
        }

        const post = {
          postId: this.postId,
          title: this.postTitle.trim(),
          content: this.postContent.JSON,
          markup: this.postContent.HTML, // Initialized as ""
          authorName: this.postData.author.name
        }

        // Validate post content
        if (isContentEmpty(this.$refs.postEditor.editor.getHTML())) {
          this.$Message.error('Content cannot empty.')
          return
        }

        post.tagsId = this.tagsId
        const updatedDate = (await axios.put(`${process.env.VUE_APP_API_URI}/forum/post/${this.postId}`, { post })).data
        this.$set(this.postData, 'title', this.postTitle)
        this.$set(this.postData, 'updatedAt', updatedDate)
        this.$set(this.postData, 'tags', this.tagsId.map(id => this.tagsList.find(item => item.id === id).name))
        this.editPost = false
      } catch (error) {
        const message = `Failed to update the post: ${error}`
        console.error(message)
        this.$Message.error(message)
      }
    },

    /**
     * Enable the edit mode for the whole post
     */
    onEditPost () {
      this.editPost = true
    },

    /**
     * Cancel edit of the whole post
     */
    onCancelEditPost () {
      this.postTitle = this.postData.title
      // Set the original Post content to the Content Editor.
      this.$set(this.postContent, 'JSON', JSON.parse(this.postData.content))
      this.$refs.postEditor.editor.setContent(this.postContent.JSON)

      this.editPost = false
    },

    /**
     * Route to forum user profile
     */
    onUserClick (user) {
      this.$router.replace({
        name: 'Forum User',
        query: {
          userId: user.id
        }
      })
    }
  }
}
</script>

<style scoped>
.forum {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

.forum .thread-content {
  margin: 0 auto;
  padding: 0 8px 32px;
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.navigation {
  padding: 8px 0;
  width: 100%;
  position: sticky;
  top: 60px;
  z-index: 100;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  background-color: var(--color-body-background);
}

.navigation .go-back,
.navigation .navigation-buttons {
  padding: 0;
  width: 50%;
  position: relative;
  display: flex;
  justify-content: flex-end;
}

.navigation .go-back {
  margin-right: 8px;
  justify-content: flex-start;
}

.navigation .go-back a {
  font-weight: bold;
  color: rgba(var(--color-black-rgb), .4);
  transition: color .3s ease-in;
}

.navigation .go-back a:hover {
  color: rgba(var(--color-black-rgb), 1);
}

.navigation >>> .ivu-btn {
  padding: 0;
  width: 40px;
}

.navigation .navigation-buttons >>> .ivu-btn:focus {
  box-shadow: none;
}

.navigation .navigation-buttons >>> .ivu-btn:hover {
  color: inherit;
  border-color: var(--color-gray-border);
}

.navigation .navigation-buttons >>> .ivu-btn:disabled {
  color: var(--color-gray-button);
}

.detail-page-wrapper {
  margin-bottom: 16px;
  padding: 16px 16px;
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  border: 1px solid var(--color-gray-border);
  background-color: rgba(var(--color-gray-button-rgb), .1);
  border-radius: 10px;
}

.detail-page-wrapper >>> .ivu-spin-fix {
  position: relative;
  height: 64px;
  color: var(--color-green-thick);
  background-color: transparent;
}

.thread-list-wrapper >>> .ivu-spin-fix p {
  margin-top: 8px;
  font-weight: bold;
}

.detail-page-wrapper .title-and-tags {
  padding-bottom: 16px;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.detail-page-wrapper .title-and-tags h1 {
  margin: 0;
  padding-bottom: 8px;
  font-size: 18px;
  width: 100%;
  display: flex;
  justify-content: flex-start;
}

.title-row-edit .title >>> input {
  font-size: 16px;
  height: 32px;
}

.title-row-edit .title >>> input {
  padding-left: 8px;
  padding-right: 32px;
}

.post-edit >>> .icons {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.post-edit >>> .icons i {
  margin-left: 16px;
  font-size: 14px;
  width: 14px;
  cursor: pointer;
}

.post-edit >>> .icons i.submit {
  color: var(--color-success);
}

.post-edit >>> .icons i.cancel {
  color: var(--color-red-border)
}

.detail-card {
  margin: 0;
  padding: 0;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.card {
  padding: 16px 0 0;
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
}

.card .avatar {
  width: 48px;
  display: flex;
  justify-content: center;
}

.card .content {
  margin: 0;
  padding: 0;
  width: calc(100% - 48px);
}

.card .content .header {
  margin: 0;
  padding: 0;
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  font-size: 14px;
  line-height: 24px;
}

.author {
  cursor: pointer;
}

.card .content .header .author,
.card .content .header .post-age {
  padding: 0 8px;
  width: 50%;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

.card .content .header .author {
  font-weight: bold;
}

.card .content .header .post-age {
  display: flex;
  justify-content: flex-end;
  font-size: 12px;
  font-style: italic;
  color: var(--color-text-content);
}

.card .content .header .post-age .reply-toolbar {
  padding-left: 8px;
}

.card .content .updated-date {
  margin: 0;
  padding-right: 8px;
  padding-bottom: 8px;
  font-style: italic;
  text-align: end;
  color: var(--color-mid-gray);
}

.detail-card .footer .reply {
  width: 56px;
}

.dummy-message {
  width: 100%;
  display: flex;
  justify-content: flex-start;
  color: var(--color-black);
}

.instruction {
  padding: 5px 0 5px 0;
  margin: 20px 0 10px 0;
  display: flex;
  flex-direction: row;
}

.instruction-text {
  font-size: 14px;
  color: var(--color-mid-gray);
  font-style: italic;
  margin-right: 5px;
}

.instruction-emoji {
  font-size: 16px;
}

@media screen and (min-width: 1024px) {
  .forum .thread-content {
    max-width: 1024px;
  }
}

@media screen and (min-width: 1440px) {
  .forum .thread-content {
    padding-left: 64px;
    padding-right: 64px;
    max-width: 1440px;
  }
}

.edit-icon {
  margin-right: 8px;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  font-weight: bold;
  color: var(--color-black);
  opacity: .7;
  transition: opacity .3s ease-in;
}

.edit-icon:hover {
  opacity: 1;
}

.post-edit {
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
}

.title,
.tags {
  margin: 3px;
}

.tags >>> .ivu-select-group-title {
  font-weight: bold;
  color: var(--color-gray-dark);
}

.no-post-msg {
  text-align: center;
  font-size: 32px;
  margin-top: 15%;
  margin-bottom: 15%;
}
</style>
